import {
  UzBadge,
  UzButton,
  type UzColumn,
  UzCheckbox,
  UzConfirmDialog,
  UzDrawer,
  UzInput,
  UzPagination,
  UzSelect,
  UzSwitch,
  UzTable,
  UzTextarea,
} from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateNotificationEvent,
  useDeleteNotificationEvent,
  useNotificationEvents,
  useUpdateNotificationEvent,
} from '../../use-notifications';

import type { NotificationEventItem } from '@utazzo/shared';

/**
 * Events tab (ADR-0048 + ADR-0049 follow-up).
 *
 * Two ways an event lands here:
 *   1. Seeded from code (`notifications-event-seeds.service.ts`) at
 *      boot. These are the canonical product events (lead.*, user.*).
 *   2. Admin-registered via "New event". These are ad-hoc codes for
 *      webhooks / one-off integrations. They land in the in-memory
 *      registry AND the DB, so publish() picks them up on the running
 *      process and survives a restart (registry reloads them from DB).
 *
 * Admin actions:
 *   - Edit name/description/samplePayload + toggle isActive
 *   - Register a new event code (snake_dot.case) via New event button
 *   - Delete an event - blocked when mandatory or any rule references it
 */
export const NotificationEventsTab = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [module, setModule] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<NotificationEventItem | null>(null);

  const query = {
    page,
    pageSize,
    sortDir: 'asc' as const,
    sortBy: 'module',
    ...(module ? { module } : {}),
    ...(activeFilter ? { isActive: activeFilter === 'active' } : {}),
  };
  const eventsQuery = useNotificationEvents(query);
  const updateEvent = useUpdateNotificationEvent();
  const deleteEvent = useDeleteNotificationEvent();

  const data = eventsQuery.data?.data ?? [];

  const moduleOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const row of data) set.add(row.module);
    const opts: ReadonlyArray<{ value: string; label: string }> = [
      { value: '', label: 'All modules' },
      ...Array.from(set)
        .sort()
        .map((m) => ({ value: m, label: m })),
    ];
    return opts;
  }, [data]);

  const columns: ReadonlyArray<UzColumn<NotificationEventItem>> = [
    {
      id: 'code',
      header: 'Event code',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.code}</span>,
      width: '240px',
    },
    {
      id: 'name',
      header: 'Display name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.name}</span>
          {row.description && <span className="text-xs text-zinc-500">{row.description}</span>}
        </div>
      ),
    },
    {
      id: 'module',
      header: 'Module',
      cell: (row) => (
        <UzBadge tone="secondary" pill>
          {row.module}
        </UzBadge>
      ),
      width: '120px',
    },
    {
      id: 'mandatory',
      header: 'Mandatory',
      cell: (row) =>
        row.isMandatory ? (
          <UzBadge tone="warning" pill>
            Required
          </UzBadge>
        ) : (
          <span className="text-xs text-zinc-400">-</span>
        ),
      width: '110px',
    },
    {
      id: 'active',
      header: 'Active',
      cell: (row) => (
        <UzSwitch
          checked={row.isActive}
          disabled={row.isMandatory || updateEvent.isPending}
          onCheckedChange={(checked) =>
            updateEvent.mutate({ id: row.id, payload: { isActive: checked } })
          }
        />
      ),
      width: '90px',
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => setDeleting(row)}
            disabled={row.isMandatory}
            title={row.isMandatory ? 'Mandatory events cannot be deleted' : 'Delete event'}
          >
            <Trash2 className="h-4 w-4" />
          </UzButton>
        </div>
      ),
      width: '70px',
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <UzSelect
          label="Module"
          value={module}
          onValueChange={(v: string) => {
            setPage(1);
            setModule(v);
          }}
          options={moduleOptions}
          containerClassName="w-44"
        />
        <UzSelect
          label="Status"
          value={activeFilter}
          onValueChange={(v: string) => {
            setPage(1);
            setActiveFilter(v);
          }}
          options={[
            { value: '', label: 'Active + inactive' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
          ]}
          containerClassName="w-44"
        />
        <div className="ml-auto">
          <UzButton size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New event
          </UzButton>
        </div>
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={eventsQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={eventsQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />

      {creating && <EventCreateDrawer onClose={() => setCreating(false)} />}

      <UzConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete event?"
        description={`Event code "${deleting?.code ?? ''}" will be removed. Any publish() call for this code will silently drop after deletion. Rules still referencing it block the delete.`}
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          if (deleting) deleteEvent.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
};

/**
 * Create-event form drawer. Lives in this file because it's small and
 * the only consumer. Sample payload is held as a JSON textarea; bad
 * JSON is rejected client-side before the round-trip.
 */
const EventCreateDrawer = ({ onClose }: { onClose: () => void }): React.JSX.Element => {
  const create = useCreateNotificationEvent();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [moduleName, setModuleName] = useState('custom');
  const [samplePayloadJson, setSamplePayloadJson] = useState('{}');
  const [isMandatory, setIsMandatory] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const submit = (): void => {
    let samplePayload: Record<string, unknown> | undefined;
    const trimmed = samplePayloadJson.trim();
    if (trimmed.length > 0) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setJsonError('Sample payload must be a JSON object.');
          return;
        }
        samplePayload = parsed as Record<string, unknown>;
      } catch (err) {
        setJsonError(`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
    }
    setJsonError(null);
    create.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        module: moduleName.trim(),
        samplePayload,
        isMandatory,
      },
      { onSuccess: onClose },
    );
  };

  const canSubmit =
    code.trim().length > 0 && name.trim().length > 0 && moduleName.trim().length > 0;

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title="Register new event"
      description="Adds a code to the engine's catalog. publish() calls for this code start working immediately and survive a restart."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </UzButton>
          <UzButton onClick={submit} disabled={create.isPending || !canSubmit}>
            Register
          </UzButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <UzInput
          label="Event code"
          placeholder="e.g. lead.first_contacted"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={create.isPending}
          required
          hint="snake_dot.case. Cannot be changed after creation."
        />
        <UzInput
          label="Display name"
          placeholder="e.g. Lead first contacted"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={create.isPending}
          required
        />
        <UzTextarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={create.isPending}
          hint="When does this event fire? Who consumes it?"
        />
        <UzInput
          label="Module"
          placeholder="e.g. leads, custom, webhooks"
          value={moduleName}
          onChange={(e) => setModuleName(e.target.value)}
          disabled={create.isPending}
          required
          hint="Grouping label. Defaults to 'custom' for admin-registered events."
        />
        <UzTextarea
          label="Sample payload (JSON)"
          rows={6}
          value={samplePayloadJson}
          onChange={(e) => {
            setSamplePayloadJson(e.target.value);
            if (jsonError) setJsonError(null);
          }}
          disabled={create.isPending}
          error={jsonError ?? undefined}
          hint="Pre-fills the Templates editor's variable box. Empty {} means no sample."
        />
        <UzCheckbox
          label="Mandatory (cannot be deactivated by users; bypasses opt-out)"
          checked={isMandatory}
          onCheckedChange={(c) => setIsMandatory(c === true)}
          disabled={create.isPending}
        />
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
          Reminder: registering an event here only adds the code to the catalog. Something still has
          to call <code>notifications.publish(&apos;{code || 'your.event'}&apos;, payload)</code> -
          either a feature module, the outbox subscriber, or the{' '}
          <code>POST /notifications/publish</code> escape hatch - or the event will sit dormant.
        </div>
      </div>
    </UzDrawer>
  );
};
